import React, { useState, useEffect, useMemo, useCallback } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  FiSearch, FiTrash2, FiUser, FiMail, FiPhone, FiCalendar,
  FiFilter, FiX, FiAlertCircle, FiShield, FiRefreshCw,
  FiMapPin, FiGrid, FiList, FiChevronRight, FiCheckCircle,
  FiEye, FiBriefcase, FiHash, FiLayers,
  FiChevronDown, FiStar, FiUserCheck, FiUserX,
  FiUserPlus, FiKey, FiLock, FiUploadCloud,
} from "react-icons/fi";

/* ─────────────────────────────────────
   CONSTANTS
───────────────────────────────────────*/
const PROFILE_BASE_URL = "https://api.ajplservices.tech/uploads/profile/";

const ROLE_META = {
  USER: { bg: "#EFF6FF", txt: "#2563EB", border: "#BFDBFE", icon: <FiUser size={10} />, label: "Standard User" },
  HOD: { bg: "#F5F3FF", txt: "#7C3AED", border: "#DDD6FE", icon: <FiShield size={10} />, label: "Head of Dept." },
  ADMIN: { bg: "#FEF3C7", txt: "#D97706", border: "#FDE68A", icon: <FiStar size={10} />, label: "System Admin" },
  WORKER: { bg: "#ECFDF5", txt: "#059669", border: "#A7F3D0", icon: <FiUserCheck size={10} />, label: "Field Worker" },
};

/* ─────────────────────────────────────
   HELPER: parse verified from any DB value
   DB sends: 1 / 0 / "1" / "0" / true / false / Buffer({data:[1]})
───────────────────────────────────────*/
const isVerifiedTrue = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "object" && val.data) return val.data[0] === 1;
  return Number(val) === 1 || val === true;
};

/* ─────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────*/
const Avatar = ({ src, name, size = 46, radius = "14px" }) => {
  const [err, setErr] = useState(false);
  const letter = (name || "?").charAt(0).toUpperCase();
  const hue = ((name || "A").charCodeAt(0) * 41) % 360;

  if (src && !err) {
    const fullSrc = src.startsWith("uploads/") ? `https://api.ajplservices.tech/${src}` : `${PROFILE_BASE_URL}${src}`;
    return (
      <img
        src={fullSrc}
        alt={name}
        onError={() => setErr(true)}
        style={{
          width: size, height: size, borderRadius: radius,
          objectFit: "cover", border: "2px solid rgba(255,255,255,0.8)",
          flexShrink: 0, display: "block",
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: `linear-gradient(135deg, hsl(${hue},60%,52%), hsl(${hue + 30},65%,45%))`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#FFF", fontWeight: 900, fontSize: size * 0.38,
      fontFamily: "'Syne', sans-serif",
    }}>
      {letter}
    </div>
  );
};

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.USER;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "5px 12px", borderRadius: "30px", fontSize: "11px", fontWeight: 800,
      background: m.bg, color: m.txt, border: `1.5px solid ${m.border}`,
      whiteSpace: "nowrap",
    }}>
      {m.icon} {role}
    </div>
  );
};

const VerifiedBadge = ({ is_verified }) => {
  const verified = isVerifiedTrue(is_verified);
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "5px 11px", borderRadius: "30px", fontSize: "11px", fontWeight: 800,
      background: verified ? "#ECFDF5" : "#FEF2F2",
      color: verified ? "#059669" : "#DC2626",
      border: `1.5px solid ${verified ? "#A7F3D0" : "#FECACA"}`,
    }}>
      {verified ? <FiCheckCircle size={11} /> : <FiUserX size={11} />}
      {verified ? "Verified" : "Unverified"}
    </div>
  );
};

const DSec = ({ title, icon, children }) => (
  <div style={{ marginBottom: "18px" }}>
    <div style={S.dsecLabel}>{icon && <span>{icon}</span>} {title}</div>
    <div style={S.dsecCard}>{children}</div>
  </div>
);

const DRow = ({ icon, label, value, mono }) => (
  <div style={S.dRow}>
    <span style={S.dRowLabel}>{icon} {label}</span>
    <span style={{ ...S.dRowVal, fontFamily: mono ? "'DM Mono', monospace" : "inherit" }}>
      {value || "—"}
    </span>
  </div>
);

const StatCard = ({ label, value, color, sub }) => (
  <div style={S.statCard}>
    <span style={S.statLabel}>{label}</span>
    <span style={{ ...S.statVal, color }}>{value}</span>
    {sub && <span style={S.statSub}>{sub}</span>}
  </div>
);

/* ─────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────*/
const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [verifyFilter, setVerifyFilter] = useState("All");
  const [viewMode, setViewMode] = useState("table");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* 🔥 ADD USER STATE */
  const [showAddModal, setShowAddModal] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [addUserForm, setAddUserForm] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    full_name: "",
    department: "",
    designation: "",
    floor_number: "",
    cabin_number: "",
    role: "USER"
  });

  /* 🔐 CHANGE PASSWORD STATE */
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  /* 🔔 CUSTOM ALERT / NOTIFICATION MODAL STATE */
  const [toastModal, setToastModal] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminService.getAllUsers();
      if (res.success) setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await AdminService.deleteUser(deleteConfirm.id);
      if (res.success) {
        const deletedName = deleteConfirm.full_name || deleteConfirm.username;
        setUsers(prev => prev.filter(u => u.id !== deleteConfirm.id));
        setDeleteConfirm(null);
        if (selectedUser?.id === deleteConfirm.id) closeDrawer();
        setToastModal({
          type: "success",
          title: "User Deleted",
          message: `${deletedName}'s account has been permanently deleted.`
        });
      } else {
        setToastModal({
          type: "error",
          title: "Delete Failed",
          message: res.message || "Could not delete user account."
        });
      }
    } catch (err) {
      setToastModal({
        type: "error",
        title: "Delete Failed",
        message: err.message || err || "Error deleting user account."
      });
    } finally {
      setDeleting(false);
    }
  };

  /* 🔥 CREATE USER HANDLER (NO OTP) */
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!addUserForm.username || !addUserForm.email || !addUserForm.phone || !addUserForm.password || !addUserForm.full_name) {
      setToastModal({
        type: "error",
        title: "Required Fields Missing",
        message: "Please fill in all required fields (*)."
      });
      return;
    }
    setSavingUser(true);
    try {
      const fd = new FormData();
      Object.keys(addUserForm).forEach(key => {
        if (addUserForm[key]) fd.append(key, addUserForm[key]);
      });
      if (profileImageFile) {
        fd.append("profile_image", profileImageFile);
      }
      const res = await AdminService.createUser(fd);
      if (res.success) {
        const createdName = addUserForm.full_name;
        setShowAddModal(false);
        setAddUserForm({
          username: "", email: "", phone: "", password: "", full_name: "",
          department: "", designation: "", floor_number: "", cabin_number: "", role: "USER"
        });
        setProfileImageFile(null);
        fetchUsers();
        setToastModal({
          type: "success",
          title: "User Account Created! 🚀",
          message: `${createdName}'s account has been created and verified successfully.`
        });
      } else {
        setToastModal({
          type: "error",
          title: "Creation Failed",
          message: res.message || "Failed to create user account."
        });
      }
    } catch (err) {
      setToastModal({
        type: "error",
        title: "Creation Error",
        message: err.message || err || "Error creating user account."
      });
    } finally {
      setSavingUser(false);
    }
  };

  /* 🔐 CHANGE PASSWORD HANDLER (NO OTP) */
  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPasswordInput || newPasswordInput.length < 4) {
      setToastModal({
        type: "error",
        title: "Password Too Short",
        message: "Password must be at least 4 characters long."
      });
      return;
    }
    setUpdatingPassword(true);
    try {
      const res = await AdminService.changeUserPassword({
        userId: changePasswordUser.id,
        newPassword: newPasswordInput
      });
      if (res.success) {
        const targetName = changePasswordUser.full_name || changePasswordUser.username;
        setChangePasswordUser(null);
        setNewPasswordInput("");
        setToastModal({
          type: "success",
          title: "Password Updated! 🔐",
          message: `Password updated successfully for ${targetName}.`
        });
      } else {
        setToastModal({
          type: "error",
          title: "Update Failed",
          message: res.message || "Failed to update password."
        });
      }
    } catch (err) {
      setToastModal({
        type: "error",
        title: "Update Error",
        message: err.message || err || "Error changing password."
      });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const openDrawer = (u) => { setSelectedUser(u); setDrawerOpen(true); };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedUser(null), 280);
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        !searchTerm ||
        u.full_name?.toLowerCase().includes(s) ||
        u.username?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s) ||
        u.phone?.includes(s) ||
        u.department?.toLowerCase().includes(s) ||
        u.designation?.toLowerCase().includes(s);

      const matchRole = roleFilter === "All" || u.role === roleFilter;

      const verified = isVerifiedTrue(u.is_verified);
      const matchVerify =
        verifyFilter === "All" ||
        (verifyFilter === "Verified" && verified) ||
        (verifyFilter === "Unverified" && !verified);

      return matchSearch && matchRole && matchVerify;
    });
  }, [users, searchTerm, roleFilter, verifyFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    users: users.filter(u => u.role === "USER").length,
    hods: users.filter(u => u.role === "HOD").length,
    admins: users.filter(u => u.role === "ADMIN").length,
    verified: users.filter(u => isVerifiedTrue(u.is_verified)).length,
    unverified: users.filter(u => !isVerifiedTrue(u.is_verified)).length,
  }), [users]);

  const activeFilters = [roleFilter !== "All", verifyFilter !== "All"].filter(Boolean).length;
  const clearFilters = () => { setRoleFilter("All"); setVerifyFilter("All"); setSearchTerm(""); };

  /* ── Loader ── */
  if (loading) return (
    <div style={S.loaderPage}>
      <style>{CSS_ANIM}</style>
      <div style={S.spinner} />
      <div style={S.loaderBrand}>ACHAL JEWELS</div>
      <div style={S.loaderSub}>Loading User Directory...</div>
      <div style={{ display: "flex", gap: "6px", marginTop: "18px" }}>
        {[0, 1, 2].map(i => <div key={i} style={{ ...S.dot, animationDelay: `${i * 0.2}s` }} />)}
      </div>
    </div>
  );

  /* ══════════════════════ RENDER ══════════════════════ */
  return (
    <>
      <style>{CSS_ANIM + CSS_HOVER}</style>
      <div style={S.layout}>
        <AdminSidebar />
        <div style={S.main}>

          {/* ══ HEADER ══ */}
          <div style={S.header}>
            <div>
              <div style={S.breadcrumb}>
                <span style={{ color: "#D4AF37", fontWeight: 800 }}>Admin</span>
                <FiChevronRight size={12} style={{ color: "#CBD5E1" }} />
                <span>User Management</span>
              </div>
              <h1 style={S.title}>User <span style={{ color: "#D4AF37" }}>Directory</span></h1>
              <p style={S.subtitle}>Manage all ecosystem participants, create users & update credentials directly</p>
            </div>
            <div style={S.headerRight}>
              <button onClick={() => setShowAddModal(true)} style={S.addBtn}>
                <FiUserPlus size={15} /> Add New User
              </button>
              <button
                onClick={() => setViewMode(v => v === "table" ? "grid" : "table")}
                style={S.iconBtn}
              >
                {viewMode === "table" ? <FiGrid size={14} /> : <FiList size={14} />}
                {viewMode === "table" ? "Grid" : "Table"}
              </button>
              <button onClick={fetchUsers} className="r-btn" style={S.refreshBtn}>
                <FiRefreshCw size={14} /> Refresh
              </button>
            </div>
          </div>

          {/* ══ STAT STRIP ══ */}
          <div style={S.statStrip}>
            <StatCard label="TOTAL MEMBERS" value={stats.total} color="#0F172A" />
            <StatCard label="STANDARD USERS" value={stats.users} color="#2563EB" />
            <StatCard label="DEPT. HODs" value={stats.hods} color="#7C3AED" />
            <StatCard label="ADMINS" value={stats.admins} color="#D97706" />
            <StatCard label="VERIFIED" value={stats.verified} color="#059669" />
            <StatCard label="UNVERIFIED" value={stats.unverified} color="#DC2626" />
          </div>

          {/* ══ SEARCH BAR ══ */}
          <div style={S.searchRow}>
            <div style={S.searchBox}>
              <FiSearch style={S.searchIcon} />
              <input
                type="text"
                placeholder="Search name, email, phone, department..."
                style={S.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} style={S.clearSearch}>
                  <FiX size={12} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(v => !v)}
              style={{
                ...S.filterToggle,
                background: showFilters || activeFilters > 0 ? "#1E293B" : "#FFF",
                color: showFilters || activeFilters > 0 ? "#FFF" : "#1E293B",
                borderColor: showFilters || activeFilters > 0 ? "#1E293B" : "#E2E8F0",
              }}
            >
              <FiFilter size={13} />
              Filters
              {activeFilters > 0 && <span style={S.filterDot}>{activeFilters}</span>}
              <FiChevronDown size={12} style={{ transform: showFilters ? "rotate(180deg)" : "none", transition: "0.2s" }} />
            </button>
            {activeFilters > 0 && (
              <button onClick={clearFilters} style={S.clearAllBtn}>
                <FiX size={11} /> Clear
              </button>
            )}
            <div style={S.resultCount}>
              <b style={{ color: "#0F172A" }}>{filteredUsers.length}</b>
              <span style={{ color: "#94A3B8" }}> / {users.length} members</span>
            </div>
          </div>

          {/* ══ FILTER PANEL ══ */}
          {showFilters && (
            <div style={S.filterPanel}>
              <div style={S.fGroup}>
                <label style={S.fLabel}><FiShield size={10} /> Role</label>
                <div style={S.selWrap}>
                  <select style={S.fSelect} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                    <option value="All">All Roles</option>
                    <option value="USER">Standard User</option>
                    <option value="HOD">Concern Person</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                  <FiChevronDown size={11} style={S.selArrow} />
                </div>
              </div>
              <div style={S.fGroup}>
                <label style={S.fLabel}><FiCheckCircle size={10} /> Verification</label>
                <div style={S.selWrap}>
                  <select style={S.fSelect} value={verifyFilter} onChange={e => setVerifyFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    <option value="Verified">Verified</option>
                    <option value="Unverified">Unverified</option>
                  </select>
                  <FiChevronDown size={11} style={S.selArrow} />
                </div>
              </div>
            </div>
          )}

          {/* ══ TABLE VIEW ══ */}
          {viewMode === "table" && (
            <div style={S.tableWrap}>
              {filteredUsers.length === 0 ? (
                <div style={S.noData}>
                  <FiSearch size={34} style={{ color: "#CBD5E1", marginBottom: "10px" }} />
                  <div style={{ fontWeight: 700, color: "#94A3B8" }}>No users found</div>
                  <div style={{ fontSize: "12px", color: "#CBD5E1", marginTop: "4px" }}>Try clearing your filters</div>
                </div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>MEMBER PROFILE</th>
                      <th style={S.th}>CONTACT</th>
                      <th style={S.th}>ROLE</th>
                      <th style={S.th}>DEPARTMENT</th>
                      <th style={S.th}>LOCATION</th>
                      <th style={S.th}>STATUS</th>
                      <th style={S.th}>JOINED</th>
                      <th style={S.th}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, idx) => (
                      <tr
                        key={u.id}
                        className="u-row"
                        style={{ animation: `fadeUp 0.3s ease ${Math.min(idx * 0.03, 0.4)}s both` }}
                      >
                        <td style={S.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Avatar src={u.profile_image} name={u.full_name || u.username} size={44} />
                            <div>
                              <div style={S.uName}>{u.full_name || "Incomplete Profile"}</div>
                              <div style={S.uUsername}>@{u.username || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={S.contactRow}><FiMail size={11} /> {u.email}</div>
                          <div style={S.contactRow}><FiPhone size={11} /> {u.phone}</div>
                        </td>
                        <td style={S.td}><RoleBadge role={u.role} /></td>
                        <td style={S.td}>
                          <div style={S.deptName}>{u.department || "—"}</div>
                          {u.designation && <div style={S.desigText}>{u.designation}</div>}
                        </td>
                        <td style={S.td}>
                          {(u.floor_number || u.cabin_number) ? (
                            <div style={S.locChip}>
                              <FiMapPin size={10} />
                              {[u.floor_number && `Floor ${u.floor_number}`, u.cabin_number && `Cabin ${u.cabin_number}`].filter(Boolean).join(" · ")}
                            </div>
                          ) : <span style={S.na}>—</span>}
                        </td>
                        <td style={S.td}><VerifiedBadge is_verified={u.is_verified} /></td>
                        <td style={S.td}>
                          <div style={S.dateText}>
                            {new Date(u.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric"
                            })}
                          </div>
                          <div style={S.timeText}>
                            {new Date(u.created_at).toLocaleTimeString("en-IN", {
                              hour: "2-digit", minute: "2-digit"
                            })}
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button onClick={() => openDrawer(u)} className="view-btn" style={S.viewBtn} title="View Profile">
                              <FiEye size={13} />
                            </button>
                            <button onClick={() => { setChangePasswordUser(u); setNewPasswordInput(""); }} className="key-btn" style={S.keyBtn} title="Change Password (No OTP)">
                              <FiKey size={13} />
                            </button>
                            <button onClick={() => setDeleteConfirm(u)} className="del-btn" style={S.deleteBtn} title="Delete Account">
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ══ GRID VIEW ══ */}
          {viewMode === "grid" && (
            <div style={S.gridWrap}>
              {filteredUsers.length === 0 ? (
                <div style={{ ...S.noData, gridColumn: "1/-1" }}>
                  <FiSearch size={34} style={{ color: "#CBD5E1", marginBottom: "10px" }} />
                  <div style={{ fontWeight: 700, color: "#94A3B8" }}>No users found</div>
                </div>
              ) : filteredUsers.map((u, idx) => (
                <div
                  key={u.id}
                  className="g-card"
                  style={{ ...S.gridCard, animationDelay: `${Math.min(idx * 0.04, 0.5)}s` }}
                >
                  <div style={{
                    height: "5px", margin: "-20px -20px 18px -20px",
                    borderRadius: "18px 18px 0 0",
                    background: u.role === "HOD" ? "linear-gradient(90deg,#7C3AED,#A78BFA)"
                      : u.role === "ADMIN" ? "linear-gradient(90deg,#D97706,#FCD34D)"
                        : "linear-gradient(90deg,#2563EB,#60A5FA)",
                  }} />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                    <Avatar src={u.profile_image} name={u.full_name || u.username} size={52} radius="15px" />
                    <VerifiedBadge is_verified={u.is_verified} />
                  </div>
                  <div style={S.gcName}>{u.full_name || "Incomplete Profile"}</div>
                  <div style={S.gcUsername}>@{u.username || "—"}</div>
                  <div style={S.gcDivider} />
                  <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "14px" }}>
                    <div style={S.gcRow}><FiMail size={11} /> {u.email}</div>
                    <div style={S.gcRow}><FiPhone size={11} /> {u.phone}</div>
                    {u.department && <div style={S.gcRow}><FiBriefcase size={11} /> {u.department}</div>}
                    {(u.floor_number || u.cabin_number) && (
                      <div style={S.gcRow}>
                        <FiMapPin size={11} />
                        {[u.floor_number && `Floor ${u.floor_number}`, u.cabin_number && `Cabin ${u.cabin_number}`].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <RoleBadge role={u.role} />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => openDrawer(u)} className="view-btn" style={S.viewBtn} title="View Profile"><FiEye size={13} /></button>
                      <button onClick={() => { setChangePasswordUser(u); setNewPasswordInput(""); }} className="key-btn" style={S.keyBtn} title="Change Password"><FiKey size={13} /></button>
                      <button onClick={() => setDeleteConfirm(u)} className="del-btn" style={S.deleteBtn} title="Delete User"><FiTrash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>{/* end main */}

        {/* ════════════════════ PROFILE DRAWER ════════════════════ */}
        {selectedUser && (
          <div style={S.overlay} onClick={closeDrawer}>
            <div
              style={{
                ...S.drawer,
                animation: drawerOpen ? "slideIn 0.3s cubic-bezier(0.4,0,0.2,1) forwards" : "none",
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Hero Banner */}
              <div style={S.drawerHero}>
                <div style={{
                  position: "absolute", inset: 0,
                  background: selectedUser.role === "HOD"
                    ? "linear-gradient(135deg,#4C1D95,#7C3AED)"
                    : selectedUser.role === "ADMIN"
                      ? "linear-gradient(135deg,#78350F,#D97706)"
                      : "linear-gradient(135deg,#1E3A5F,#2563EB)",
                }} />
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "relative", padding: "26px 26px 22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={S.heroEyebrow}><FiUser size={11} /> User Profile</div>
                    <button onClick={closeDrawer} style={S.closeBtnHero}><FiX size={17} /></button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "16px" }}>
                    <Avatar src={selectedUser.profile_image} name={selectedUser.full_name || selectedUser.username} size={66} radius="20px" />
                    <div style={{ flex: 1 }}>
                      <div style={S.heroName}>{selectedUser.full_name || "Incomplete Profile"}</div>
                      <div style={S.heroUsername}>@{selectedUser.username || "—"}</div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
                        <RoleBadge role={selectedUser.role} />
                        <VerifiedBadge is_verified={selectedUser.is_verified} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Drawer Body */}
              <div style={S.drawerBody}>

                {/* Contact */}
                <DSec title="Contact Information" icon={<FiPhone size={11} />}>
                  <DRow icon={<FiMail size={11} />} label="Email" value={selectedUser.email} />
                  <DRow icon={<FiPhone size={11} />} label="Phone" value={selectedUser.phone} />
                  <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                    <button onClick={() => window.location.href = `tel:${selectedUser.phone}`} style={S.cAction}>
                      <FiPhone size={13} /> Call
                    </button>
                    <button onClick={() => window.open(`https://wa.me/${selectedUser.phone}`, "_blank")} style={{ ...S.cAction, background: "#F0FDF4", color: "#16A34A" }}>
                      <FiUser size={13} /> WhatsApp
                    </button>
                    <button onClick={() => window.location.href = `mailto:${selectedUser.email}`} style={{ ...S.cAction, background: "#EFF6FF", color: "#2563EB" }}>
                      <FiMail size={13} /> Email
                    </button>
                  </div>
                </DSec>

                {/* Account Details */}
                <DSec title="Account Details" icon={<FiHash size={11} />}>
                  <DRow icon={<FiHash size={11} />} label="User ID" value={`#${selectedUser.id}`} mono />
                  <DRow icon={<FiUser size={11} />} label="Username" value={`@${selectedUser.username || "—"}`} mono />
                  <DRow icon={<FiShield size={11} />} label="Role" value={ROLE_META[selectedUser.role]?.label || selectedUser.role} />
                  <DRow icon={<FiCheckCircle size={11} />} label="Verified" value={isVerifiedTrue(selectedUser.is_verified) ? "Email Verified ✓" : "Not Verified"} />
                  <DRow icon={<FiCalendar size={11} />} label="Joined On" value={
                    new Date(selectedUser.created_at).toLocaleString("en-IN", {
                      day: "2-digit", month: "long", year: "numeric",
                      hour: "2-digit", minute: "2-digit"
                    })
                  } />
                </DSec>

                {/* Professional */}
                <DSec title="Professional Details" icon={<FiBriefcase size={11} />}>
                  <DRow icon={<FiBriefcase size={11} />} label="Department" value={selectedUser.department} />
                  <DRow icon={<FiLayers size={11} />} label="Designation" value={selectedUser.designation} />
                  {(selectedUser.floor_number || selectedUser.cabin_number) && (
                    <DRow
                      icon={<FiMapPin size={11} />}
                      label="Location"
                      value={[
                        selectedUser.floor_number && `Floor ${selectedUser.floor_number}`,
                        selectedUser.cabin_number && `Cabin ${selectedUser.cabin_number}`,
                      ].filter(Boolean).join(" — ")}
                    />
                  )}
                </DSec>

                {/* Quick Security Action */}
                <div style={{ marginBottom: "18px" }}>
                  <button
                    onClick={() => { setChangePasswordUser(selectedUser); setNewPasswordInput(""); closeDrawer(); }}
                    style={S.passBtn}
                  >
                    <FiKey size={14} /> Change Password (Direct / No OTP)
                  </button>
                </div>

                {/* Danger Zone */}
                <div style={S.dangerZone}>
                  <div style={S.dangerTitle}><FiAlertCircle size={14} /> Danger Zone</div>
                  <div style={S.dangerDesc}>Permanently deletes this user's account and all associated data. This cannot be undone.</div>
                  <button
                    onClick={() => { setDeleteConfirm(selectedUser); closeDrawer(); }}
                    style={S.dangerBtn}
                  >
                    <FiTrash2 size={13} /> Delete User Account
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ════════════════════ ➕ ADD USER MODAL ════════════════════ */}
        {showAddModal && (
          <div style={S.modalOverlay} onClick={() => !savingUser && setShowAddModal(false)}>
            <div style={{ ...S.modalCard, width: "560px", textAlign: "left" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <div>
                  <h3 style={{ ...S.modalTitle, margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                    <FiUserPlus color="#D4AF37" size={20} /> Create New User
                  </h3>
                  <p style={{ ...S.modalDesc, margin: "4px 0 0" }}>
                    Fill details to create a verified user account directly (No OTP required)
                  </p>
                </div>
                <button onClick={() => setShowAddModal(false)} style={S.closeBtnModal}><FiX size={16} /></button>
              </div>

              <form onSubmit={handleCreateUser}>
                <div style={S.formGrid}>
                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Test User"
                      style={S.formInput}
                      value={addUserForm.full_name}
                      onChange={e => setAddUserForm({ ...addUserForm, full_name: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Username *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. test_s"
                      style={S.formInput}
                      value={addUserForm.username}
                      onChange={e => setAddUserForm({ ...addUserForm, username: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="e.g. user@achaljewels.com"
                      style={S.formInput}
                      value={addUserForm.email}
                      onChange={e => setAddUserForm({ ...addUserForm, email: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Phone Number *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 9876543210"
                      style={S.formInput}
                      value={addUserForm.phone}
                      onChange={e => setAddUserForm({ ...addUserForm, phone: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Password *</label>
                    <input
                      type="password"
                      required
                      placeholder="Enter new password"
                      style={S.formInput}
                      value={addUserForm.password}
                      onChange={e => setAddUserForm({ ...addUserForm, password: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Role</label>
                    <select
                      style={S.formSelect}
                      value={addUserForm.role}
                      onChange={e => setAddUserForm({ ...addUserForm, role: e.target.value })}
                    >
                      <option value="USER">Standard User</option>
                      <option value="HOD">Concern Person (HOD)</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Department</label>
                    <input
                      type="text"
                      placeholder="e.g. IT & Tech Support"
                      style={S.formInput}
                      value={addUserForm.department}
                      onChange={e => setAddUserForm({ ...addUserForm, department: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Designation</label>
                    <input
                      type="text"
                      placeholder="e.g. Senior Software Engineer"
                      style={S.formInput}
                      value={addUserForm.designation}
                      onChange={e => setAddUserForm({ ...addUserForm, designation: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Floor Number</label>
                    <input
                      type="text"
                      placeholder="e.g. 2nd Floor"
                      style={S.formInput}
                      value={addUserForm.floor_number}
                      onChange={e => setAddUserForm({ ...addUserForm, floor_number: e.target.value })}
                    />
                  </div>

                  <div style={S.inputGroup}>
                    <label style={S.formLabel}>Cabin Number</label>
                    <input
                      type="text"
                      placeholder="e.g. Cabin 204"
                      style={S.formInput}
                      value={addUserForm.cabin_number}
                      onChange={e => setAddUserForm({ ...addUserForm, cabin_number: e.target.value })}
                    />
                  </div>

                  <div style={{ ...S.inputGroup, gridColumn: "1/-1" }}>
                    <label style={S.formLabel}>Profile Photo (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      style={S.formInput}
                      onChange={e => setProfileImageFile(e.target.files[0] || null)}
                    />
                  </div>
                </div>

                <div style={{ ...S.modalActions, marginTop: "24px" }}>
                  <button type="button" style={S.cancelBtn} onClick={() => setShowAddModal(false)} disabled={savingUser}>
                    Cancel
                  </button>
                  <button type="submit" style={{ ...S.saveBtn, opacity: savingUser ? 0.7 : 1 }} disabled={savingUser}>
                    {savingUser ? "Creating User..." : "Create User Account"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ════════════════════ 🔐 CHANGE PASSWORD MODAL ════════════════════ */}
        {changePasswordUser && (
          <div style={S.modalOverlay} onClick={() => !updatingPassword && setChangePasswordUser(null)}>
            <div style={{ ...S.modalCard, width: "420px", textAlign: "left" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ ...S.modalTitle, margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                  <FiKey color="#D97706" size={20} /> Change Password
                </h3>
                <button onClick={() => setChangePasswordUser(null)} style={S.closeBtnModal}><FiX size={16} /></button>
              </div>

              <div style={S.modalPreview}>
                <Avatar src={changePasswordUser.profile_image} name={changePasswordUser.full_name || changePasswordUser.username} size={42} radius="12px" />
                <div>
                  <div style={S.modalPreviewName}>{changePasswordUser.full_name || "Unknown"}</div>
                  <div style={S.modalPreviewEmail}>@{changePasswordUser.username || changePasswordUser.email}</div>
                </div>
              </div>

              <form onSubmit={handleChangePasswordSubmit}>
                <div style={{ marginBottom: "20px" }}>
                  <label style={S.formLabel}>New Password * (Min 4 chars)</label>
                  <input
                    type="password"
                    required
                    placeholder="Enter new password for this user"
                    style={{ ...S.formInput, marginTop: "6px" }}
                    value={newPasswordInput}
                    onChange={e => setNewPasswordInput(e.target.value)}
                  />
                  <div style={{ fontSize: "11px", color: "#94A3B8", marginTop: "6px" }}>
                    ⚡ Password will be updated directly in the database without OTP verification.
                  </div>
                </div>

                <div style={S.modalActions}>
                  <button type="button" style={S.cancelBtn} onClick={() => setChangePasswordUser(null)} disabled={updatingPassword}>
                    Cancel
                  </button>
                  <button type="submit" style={{ ...S.saveBtn, background: "linear-gradient(135deg,#D97706,#B45309)", opacity: updatingPassword ? 0.7 : 1 }} disabled={updatingPassword}>
                    {updatingPassword ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ════════════════════ DELETE MODAL ════════════════════ */}
        {deleteConfirm && (
          <div style={S.modalOverlay} onClick={() => !deleting && setDeleteConfirm(null)}>
            <div style={S.modalCard} onClick={e => e.stopPropagation()}>
              <div style={S.modalIconWrap}>
                <div style={S.modalIconCircle}><FiTrash2 size={24} color="#FFF" /></div>
              </div>
              <div style={S.modalPreview}>
                <Avatar src={deleteConfirm.profile_image} name={deleteConfirm.full_name || deleteConfirm.username} size={44} radius="12px" />
                <div>
                  <div style={S.modalPreviewName}>{deleteConfirm.full_name || "Unknown"}</div>
                  <div style={S.modalPreviewEmail}>{deleteConfirm.email}</div>
                </div>
              </div>
              <h3 style={S.modalTitle}>Terminate User Access?</h3>
              <p style={S.modalDesc}>
                This will permanently delete <strong>{deleteConfirm.full_name || deleteConfirm.username}</strong>'s account and all associated data. This action <strong>cannot be reversed</strong>.
              </p>
              <div style={S.modalActions}>
                <button style={S.cancelBtn} onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
                <button style={{ ...S.confirmBtn, opacity: deleting ? 0.7 : 1 }} onClick={handleDelete} disabled={deleting}>
                  {deleting ? "Deleting..." : <><FiTrash2 size={13} /> Confirm Delete</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════ 🔔 CUSTOM TOAST NOTIFICATION MODAL ════════════════════ */}
        {toastModal && (
          <div style={S.modalOverlay} onClick={() => setToastModal(null)}>
            <div style={{ ...S.modalCard, width: "380px" }} onClick={e => e.stopPropagation()}>
              <div style={S.modalIconWrap}>
                <div style={{
                  ...S.modalIconCircle,
                  background: toastModal.type === "success"
                    ? "linear-gradient(135deg,#10B981,#059669)"
                    : "linear-gradient(135deg,#EF4444,#DC2626)",
                  boxShadow: toastModal.type === "success"
                    ? "0 4px 14px rgba(16,185,129,0.4)"
                    : "0 4px 14px rgba(239,68,68,0.4)"
                }}>
                  {toastModal.type === "success" ? <FiCheckCircle size={26} color="#FFF" /> : <FiAlertCircle size={26} color="#FFF" />}
                </div>
              </div>
              <h3 style={{ ...S.modalTitle, marginTop: "12px" }}>{toastModal.title}</h3>
              <p style={{ ...S.modalDesc, marginTop: "8px", fontSize: "13px" }}>{toastModal.message}</p>
              <div style={{ ...S.modalActions, justifyContent: "center", marginTop: "20px" }}>
                <button
                  style={{
                    ...S.saveBtn,
                    background: toastModal.type === "success"
                      ? "linear-gradient(135deg,#10B981,#059669)"
                      : "linear-gradient(135deg,#64748B,#475569)",
                    padding: "10px 28px",
                    width: "100%"
                  }}
                  onClick={() => setToastModal(null)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

/* ─────────────────────────────────────
   CSS
───────────────────────────────────────*/
const CSS_ANIM = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { transform:translateX(100%); } to { transform:translateX(0); } }
  @keyframes pulse   { 0%,100%{ opacity:1; } 50%{ opacity:0.3; } }
  @keyframes popIn   { from { opacity:0; transform:scale(0.9) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }
`;
const CSS_HOVER = `
  .u-row:hover td { background:#FAFBFE !important; }
  .u-row .view-btn,.u-row .del-btn,.u-row .key-btn { transition:all 0.18s; }
  .u-row:hover .view-btn { background:#D4AF37 !important; color:#000 !important; }
  .g-card { transition:transform 0.22s,box-shadow 0.22s; }
  .g-card:hover { transform:translateY(-4px); box-shadow:0 24px 48px rgba(0,0,0,0.1) !important; }
  .view-btn:hover { background:#D4AF37 !important; color:#000 !important; }
  .key-btn:hover  { background:#D97706 !important; color:#FFF !important; }
  .del-btn:hover  { background:#DC2626 !important; color:#FFF !important; }
  .r-btn { transition:all 0.2s; }
  .r-btn:hover { background:#D4AF37 !important; color:#000 !important; border-color:#D4AF37 !important; }
  ::-webkit-scrollbar { width:8px; height:8px; }
  ::-webkit-scrollbar-track { background:#F1F5F9; border-radius:99px; }
  ::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:99px; }
  ::-webkit-scrollbar-thumb:hover { background:#D4AF37; }
`;

/* ─────────────────────────────────────
   STYLES
───────────────────────────────────────*/
const S = {
  layout: { display: "flex", background: "#F0F3F8", minHeight: "100vh", fontFamily: "'DM Sans',sans-serif", overflowX: "hidden" },
  main: { marginLeft: "260px", padding: "32px 40px", flex: 1, minWidth: 0, maxWidth: "calc(100vw - 260px)", boxSizing: "border-box" },

  /* Header */
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" },
  breadcrumb: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#94A3B8", fontWeight: 600, marginBottom: "8px" },
  title: { margin: 0, fontSize: "30px", fontWeight: 900, color: "#0F172A", letterSpacing: "-0.8px", fontFamily: "'Syne',sans-serif" },
  subtitle: { margin: "4px 0 0", fontSize: "13px", color: "#94A3B8", fontWeight: 500 },
  headerRight: { display: "flex", gap: "12px", alignItems: "center" },
  addBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "11px 20px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#D4AF37,#B38F26)", color: "#000", fontWeight: 800, cursor: "pointer", fontSize: "13px", boxShadow: "0 4px 14px rgba(212,175,55,0.3)" },
  iconBtn: { display: "flex", alignItems: "center", gap: "7px", padding: "11px 18px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },
  refreshBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "11px 20px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },

  /* Stats */
  statStrip: { display: "flex", gap: "12px", marginBottom: "22px", overflowX: "auto", paddingBottom: "4px" },
  statCard: { background: "#FFF", borderRadius: "16px", padding: "15px 20px", border: "1.5px solid #F1F5F9", flexShrink: 0, minWidth: "105px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" },
  statLabel: { display: "block", fontSize: "9px", fontWeight: 800, color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" },
  statVal: { display: "block", fontSize: "26px", fontWeight: 900, marginTop: "4px" },
  statSub: { display: "block", fontSize: "10px", color: "#CBD5E1", marginTop: "2px" },

  /* Search */
  searchRow: { display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" },
  searchBox: { position: "relative", flex: 1, minWidth: "240px", maxWidth: "480px" },
  searchIcon: { position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8" },
  searchInput: { width: "100%", padding: "12px 38px 12px 42px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", background: "#FFF", fontSize: "13px", fontWeight: 500, color: "#1E293B", transition: "border-color 0.2s", boxSizing: "border-box" },
  clearSearch: { position: "absolute", right: "11px", top: "50%", transform: "translateY(-50%)", background: "#F1F5F9", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
  filterToggle: { display: "flex", alignItems: "center", gap: "7px", padding: "11px 18px", borderRadius: "12px", border: "1.5px solid", fontWeight: 700, cursor: "pointer", fontSize: "13px", transition: "0.2s", whiteSpace: "nowrap" },
  filterDot: { background: "#D4AF37", color: "#000", borderRadius: "50%", width: "17px", height: "17px", fontSize: "10px", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" },
  clearAllBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "10px 14px", borderRadius: "12px", border: "1.5px solid #FCA5A5", background: "#FEF2F2", color: "#EF4444", fontWeight: 700, cursor: "pointer", fontSize: "12px", whiteSpace: "nowrap" },
  resultCount: { fontSize: "13px", marginLeft: "auto", whiteSpace: "nowrap" },

  /* Filter panel */
  filterPanel: {
    background: "#FFF", borderRadius: "16px", padding: "18px 22px",
    border: "1.5px solid #E2E8F0", marginBottom: "16px",
    display: "flex", gap: "24px", flexWrap: "wrap",
    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
    animation: "fadeUp 0.2s ease",
  },
  fGroup: { display: "flex", flexDirection: "column", gap: "6px", minWidth: "180px" },
  fLabel: { fontSize: "10px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", display: "flex", alignItems: "center", gap: "5px" },
  selWrap: { position: "relative" },
  fSelect: { width: "100%", padding: "10px 30px 10px 13px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", fontWeight: 600, color: "#1E293B", cursor: "pointer", appearance: "none", outline: "none" },
  selArrow: { position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", pointerEvents: "none" },

  /* Table */
  tableWrap: { background: "#FFF", borderRadius: "22px", padding: "12px 16px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1.5px solid #F1F5F9", overflowX: "auto", width: "100%", boxSizing: "border-box" },
  table: { width: "100%", minWidth: "1150px", borderCollapse: "separate", borderSpacing: "0 2px" },
  th: { padding: "13px 16px", textAlign: "left", fontSize: "10px", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 800, whiteSpace: "nowrap" },
  td: { padding: "15px 16px", verticalAlign: "middle", borderBottom: "1.5px solid #F8FAFC" },

  uName: { fontWeight: 800, color: "#0F172A", fontSize: "14px" },
  uUsername: { fontSize: "11px", color: "#94A3B8", fontFamily: "'DM Mono',monospace", marginTop: "2px" },
  contactRow: { fontSize: "12px", color: "#475569", display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", fontWeight: 600 },
  deptName: { fontWeight: 700, color: "#334155", fontSize: "13px" },
  desigText: { fontSize: "11px", color: "#94A3B8", marginTop: "2px" },
  locChip: { display: "inline-flex", alignItems: "center", gap: "5px", background: "#F1F5F9", color: "#475569", padding: "4px 10px", borderRadius: "8px", fontSize: "11px", fontWeight: 700 },
  na: { color: "#CBD5E1", fontSize: "12px" },
  dateText: { fontSize: "13px", fontWeight: 700, color: "#334155" },
  timeText: { fontSize: "11px", color: "#94A3B8", marginTop: "2px" },
  viewBtn: { background: "#F1F5F9", color: "#475569", border: "none", width: "36px", height: "36px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  keyBtn: { background: "#FEF3C7", color: "#D97706", border: "none", width: "36px", height: "36px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  deleteBtn: { background: "#FEF2F2", color: "#EF4444", border: "none", width: "36px", height: "36px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  noData: { padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },

  /* Grid */
  gridWrap: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(268px,1fr))", gap: "16px" },
  gridCard: { background: "#FFF", borderRadius: "18px", padding: "20px", border: "1.5px solid #F1F5F9", cursor: "default", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", animation: "fadeUp 0.3s ease both" },
  gcName: { fontWeight: 800, color: "#0F172A", fontSize: "16px", marginTop: "2px" },
  gcUsername: { fontSize: "12px", color: "#94A3B8", fontFamily: "'DM Mono',monospace", marginBottom: "12px", marginTop: "3px" },
  gcDivider: { height: "1.5px", background: "#F1F5F9", margin: "12px 0" },
  gcRow: { fontSize: "12px", color: "#475569", display: "flex", alignItems: "center", gap: "7px", fontWeight: 600 },

  /* Drawer */
  overlay: { position: "fixed", inset: 0, background: "rgba(8,14,30,0.46)", zIndex: 1000, display: "flex", justifyContent: "flex-end", backdropFilter: "blur(8px)" },
  drawer: { width: "500px", maxWidth: "95vw", height: "100%", background: "#F4F6FA", boxShadow: "-30px 0 80px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" },
  drawerHero: { position: "relative", overflow: "hidden", flexShrink: 0 },
  heroEyebrow: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "rgba(255,255,255,0.7)", fontWeight: 800, textTransform: "uppercase", letterSpacing: "1.5px", background: "rgba(255,255,255,0.12)", padding: "4px 12px", borderRadius: "30px" },
  heroName: { fontSize: "20px", fontWeight: 900, color: "#FFF", marginTop: "4px", fontFamily: "'Syne',sans-serif" },
  heroUsername: { fontSize: "13px", color: "rgba(255,255,255,0.65)", fontFamily: "'DM Mono',monospace" },
  closeBtnHero: { background: "rgba(255,255,255,0.15)", border: "none", width: "38px", height: "38px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF", flexShrink: 0 },
  closeBtnModal: { background: "#F1F5F9", border: "none", width: "32px", height: "32px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
  drawerBody: { flex: 1, overflowY: "auto", padding: "20px 24px" },

  dsecLabel: { fontSize: "10px", color: "#94A3B8", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" },
  dsecCard: { background: "#FFF", padding: "16px 18px", borderRadius: "18px", border: "1.5px solid #EEF2F8", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" },
  dRow: { display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "9px", fontSize: "13px" },
  dRowLabel: { display: "flex", alignItems: "center", gap: "6px", minWidth: "120px", color: "#94A3B8", fontWeight: 700, fontSize: "12px", paddingTop: "1px" },
  dRowVal: { fontWeight: 700, color: "#1E293B", flex: 1, lineHeight: "1.5" },

  cAction: { display: "flex", alignItems: "center", gap: "7px", padding: "9px 16px", borderRadius: "11px", border: "none", background: "#F1F5F9", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: "12px" },
  passBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", borderRadius: "12px", border: "1.5px solid #FDE68A", background: "#FEF3C7", color: "#D97706", fontWeight: 800, cursor: "pointer", fontSize: "13px", width: "100%", justifyContent: "center" },

  dangerZone: { background: "#FFF5F5", border: "1.5px solid #FECACA", borderRadius: "18px", padding: "16px 18px", marginTop: "16px" },
  dangerTitle: { display: "flex", alignItems: "center", gap: "8px", fontWeight: 800, color: "#DC2626", fontSize: "14px", marginBottom: "8px" },
  dangerDesc: { fontSize: "13px", color: "#B91C1C", lineHeight: "1.6", marginBottom: "14px" },
  dangerBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", borderRadius: "12px", border: "none", background: "#DC2626", color: "#FFF", fontWeight: 800, cursor: "pointer", fontSize: "13px", width: "100%", justifyContent: "center" },

  /* Form controls */
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" },
  inputGroup: { display: "flex", flexDirection: "column", gap: "5px" },
  formLabel: { fontSize: "12px", fontWeight: 700, color: "#475569" },
  formInput: { width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E2E8F0", outline: "none", fontSize: "13px", fontWeight: 500, color: "#1E293B", boxSizing: "border-box" },
  formSelect: { width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E2E8F0", outline: "none", fontSize: "13px", fontWeight: 600, color: "#1E293B", background: "#FFF", cursor: "pointer", boxSizing: "border-box" },
  saveBtn: { padding: "13px 22px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#0F172A,#1E293B)", color: "#FFF", fontWeight: 800, cursor: "pointer", fontSize: "13px", boxShadow: "0 4px 14px rgba(15,23,42,0.2)" },

  /* Modal */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(8,14,30,0.46)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center", backdropFilter: "blur(10px)" },
  modalCard: { background: "#FFF", borderRadius: "28px", padding: "30px 28px", width: "410px", maxWidth: "92vw", textAlign: "center", boxShadow: "0 30px 80px rgba(0,0,0,0.18)", animation: "popIn 0.3s cubic-bezier(0.4,0,0.2,1)", maxHeight: "90vh", overflowY: "auto" },
  modalIconWrap: { display: "flex", justifyContent: "center", marginBottom: "18px" },
  modalIconCircle: { width: "60px", height: "60px", borderRadius: "18px", background: "linear-gradient(135deg,#EF4444,#DC2626)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(220,38,38,0.3)" },
  modalPreview: { display: "flex", alignItems: "center", gap: "12px", background: "#F8FAFC", borderRadius: "14px", padding: "12px 14px", marginBottom: "18px", border: "1.5px solid #F1F5F9" },
  modalPreviewName: { fontWeight: 800, color: "#0F172A", fontSize: "14px" },
  modalPreviewEmail: { fontSize: "12px", color: "#94A3B8", marginTop: "2px" },
  modalTitle: { fontSize: "20px", fontWeight: 900, color: "#0F172A", margin: "0 0 10px", fontFamily: "'Syne',sans-serif" },
  modalDesc: { fontSize: "13px", color: "#64748B", lineHeight: "1.7", marginBottom: "20px" },
  modalActions: { display: "flex", gap: "12px", justifyContent: "flex-end" },
  cancelBtn: { padding: "13px 20px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },
  confirmBtn: { padding: "13px 20px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#FFF", fontWeight: 800, cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxShadow: "0 6px 20px rgba(220,38,38,0.3)" },

  /* Loader */
  loaderPage: { height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "#F0F3F8", fontFamily: "'DM Sans',sans-serif" },
  spinner: { width: "46px", height: "46px", border: "5px solid #E2E8F0", borderTopColor: "#D4AF37", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loaderBrand: { marginTop: "22px", fontSize: "16px", fontWeight: 900, color: "#1E293B", letterSpacing: "5px", fontFamily: "'Syne',sans-serif" },
  loaderSub: { color: "#94A3B8", fontSize: "12px", marginTop: "5px" },
  dot: { width: "7px", height: "7px", borderRadius: "50%", background: "#D4AF37", animation: "pulse 1.2s ease infinite" },
};

export default Users;
